import React from 'react';

import { ACTIONS, ButtonComponentType, ButtonType, MODALS, ModalType } from './types';
import { WaitModal } from './modals/WaitModal';
import { PrivateModal } from './modals/PrivateModal';
import PermissionModal from './modals/PermissionModal';
import UninstallModal from './modals/UninstallModal';
import { UninstallTileModal } from './modals/UninstallTileModal';
import { TooFewTilesModal } from './modals/TooFewTilesModal';
import TooManyTilesModal from './modals/TooManyTilesModal';
import { InstallTileAndAppModal } from './modals/InstallTileAndAppModal';
import App from '../../../models/app';
import add_icon from '../../../icons/add.svg';
import remove_icon from '../../../icons/remove.svg';
import { fitbitDarkGreen, fitbitLightGray, fitbitPremiumYellow } from '../../../themes';
import { AppType, maxTileNumber, minTileNumber } from '../../../constants';
import { DisabledButton, LoadingBar, Progress } from './Common';
import AppIsPaidModal from './modals/AppIsPaidModal';

const appInstalledTilesCount = (app: App, tiles: App[]) => {
  const tilesOwned = tiles.filter((tile) => tile.parentApp?.id === app.id);

  return tilesOwned.length;
}

export const modals: ModalType[] = [
  {
    ModalComponent: WaitModal,
    name: MODALS.WAIT,
    shouldDisplay: (app, { currentlySyncing, requiresSync }) =>
      (app.isClock && currentlySyncing && requiresSync),
  },
  {
    ModalComponent: PrivateModal,
    name: MODALS.PRIVATE,
    shouldDisplay: (app) => (app.isPrivate && !app.hasUpdate && !app.bypassPrivateAppWarning),
  },
  {
    ModalComponent: AppIsPaidModal,
    name: MODALS.APP_IS_PAID,
    shouldDisplay: (app) => (app.isPaid),
  },
  {
    ModalComponent: PermissionModal,
    name: MODALS.PERMISSION,
    shouldDisplay: (app: App, options: any) => (app.type.toLocaleLowerCase() !== AppType.TILE && !app.arePermissionsReady) ||
      (app.type.toLocaleLowerCase() === AppType.TILE && !options.parentApp),
  },
  {
    ModalComponent: TooFewTilesModal,
    name: MODALS.TOO_FEW_TILES,
    shouldDisplay: (app, { tiles, features }) => {
      const currentTiles = tiles || [];
      let minTileCountRespected = true;

      if(features?.flags?.enable_tiles !== true) {
        return false;
      }

      if(app.type.toLocaleLowerCase() === AppType.APP) {
        const ownedTilesNumber = appInstalledTilesCount(app, currentTiles);
        minTileCountRespected = ownedTilesNumber === 0 || (currentTiles.length - ownedTilesNumber) >= minTileNumber;
      }

      return (app.type.toLocaleLowerCase() === AppType.TILE && (!tiles || tiles.length <= minTileNumber)) ||
       (app.type.toLocaleLowerCase() === AppType.APP && !minTileCountRespected);
    }
  },
  {
    ModalComponent: TooManyTilesModal,
    name: MODALS.TOO_MANY_TILES,
    shouldDisplay: (app, { tiles }) => {
      const currentTiles = tiles || [];

      return (app.type.toLocaleLowerCase() === AppType.TILE && currentTiles.length >= maxTileNumber)
    }
  },
  {
    ModalComponent: UninstallModal,
    name: MODALS.UNINSTALL,
    shouldDisplay: (app) => (app.isInstalled || app.hasUpdateAvailable) && app.type.toLocaleLowerCase() !== AppType.TILE,
  },
  {
    ModalComponent: UninstallTileModal,
    name: MODALS.UNINSTALL_TILE,
    shouldDisplay: (app, { tiles }) => app.type.toLocaleLowerCase() === AppType.TILE && (!!tiles && tiles.length > minTileNumber)
  },
  {
    ModalComponent: InstallTileAndAppModal,
    name: MODALS.INSTALL_TILE_AND_APP,
    shouldDisplay: (app: App, options: any) => app.type.toLocaleLowerCase() === AppType.TILE && !options.parentAppInstalled
  }
];

export const buttonComponents: ButtonComponentType[] = [
  {
    shouldDisplay: (app: App) => app.showProgress,
    ButtonComponent: (app: App) => (
      <Progress progress={app.progress} color={app.isPremium ? fitbitPremiumYellow : undefined} />
    ),
    name: ButtonType.PROGRESS,
  },
  {
    shouldDisplay: (app: App, { loading }) => (
        ((app.isPending ||
        app.isPendingRemoval ||
        (loading && app.isInstalling) ||
        (app.isClock && app.isSelected && !app.isActive)) && (!app.isTile || (app.isTile && !app.cannotInstallOnDevice)))
    ),
    ButtonComponent: (app: App) => (
      <LoadingBar color={app.isPremium ? fitbitPremiumYellow : undefined} />
    ),
    name: ButtonType.LOADING,
  },
  {
    shouldDisplay: (app: App, { action }) => {
      // if the button is meant for uninstall dont display the update button
      if (action === ACTIONS.UNINSTALL) {
        return false;
      }
      return app.hasUpdateAvailable;
    },
    action: ACTIONS.UPDATE,
    textKey: 'UPDATE_AVAILABLE',
    props: {
      id: 'update-button',
      backgroundColor: fitbitDarkGreen,
      color: '#ffffff',
    },
    modals: [MODALS.WAIT, MODALS.PRIVATE, MODALS.PERMISSION],
    name: ButtonType.UPDATE,
  },
  {
    shouldDisplay: (app: App, options: any) => (
      app.isOnDevice && (options.onUninstall && options.showRemove)
    ),
    action: ACTIONS.UNINSTALL,
    textKey: 'UNINSTALL',
    props: {
      id: 'uninstall-button',
      backgroundColor: fitbitLightGray,
      color: fitbitDarkGreen,
      icon: remove_icon,
    },
    modals: [MODALS.TOO_FEW_TILES, MODALS.UNINSTALL, MODALS.UNINSTALL_TILE],
    name: ButtonType.UNINSTALL,
  },
  {
    shouldDisplay: (app: App) => (
      app.isOnDevice && ([AppType.APP, AppType.SERVICE].includes(app.type.toLowerCase() as any) || app.isActive)
    ),
    ButtonComponent: (app, { texts }) => (
      <DisabledButton id="uninstall-button-disabled" aria-label="uninstall-button-disabled">{texts['INSTALLED']}</DisabledButton>
    ),
    name: ButtonType.DISABLED,
  },
  {
    shouldDisplay: (app: App) => (
      app.hasNoVersionAvailable || app.cannotInstallOnDevice
    ),
    ButtonComponent: (app, { texts }) => (
      <DisabledButton>{texts['DISABLED']}</DisabledButton>
    ),
    name: ButtonType.DISABLED,
  },
  {
    shouldDisplay: () => true,
    action: ACTIONS.INSTALL,
    textKey: 'NOT_INSTALLED',
    props: (app) => ({
      icon: add_icon,
      backgroundColor: app.isPremium ? fitbitPremiumYellow : fitbitDarkGreen,
      color: '#ffffff',
      id: 'install-button',
    }),
    modals: [MODALS.APP_IS_PAID, MODALS.WAIT, MODALS.PRIVATE, MODALS.TOO_MANY_TILES, MODALS.INSTALL_TILE_AND_APP, MODALS.PERMISSION],
    name: ButtonType.INSTALL,
  }
];
